import { useEffect, useState } from "react";
import { useLocation, useParams } from "wouter";
import { useForm } from "react-hook-form";
import api from "@/lib/api";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { ArrowLeft } from "lucide-react";
import { Link } from "wouter";

interface FormData {
  slug: string;
  title: string;
  excerpt: string;
  contentText: string;
  category: string;
  date: string;
  readTime: number;
  image: string;
  author: string;
  featured: boolean;
}

export default function BlogPostForm() {
  const params = useParams<{ id: string }>();
  const isEdit = params.id && params.id !== "new";
  const [, navigate] = useLocation();
  const [saving, setSaving] = useState(false);

  const { register, handleSubmit, setValue, watch, reset } = useForm<FormData>({
    defaultValues: {
      slug: "",
      title: "",
      excerpt: "",
      contentText: "",
      category: "seo",
      date: new Date().toISOString().split("T")[0],
      readTime: 5,
      image: "",
      author: "צוות אפסייט",
      featured: false,
    },
  });

  const imageValue = watch("image");

  useEffect(() => {
    if (isEdit) {
      api.get(`/blog-posts`).then(({ data }) => {
        const post = data.find((p: any) => p.id === parseInt(params.id!));
        if (post) {
          reset({
            slug: post.slug,
            title: post.title,
            excerpt: post.excerpt,
            contentText: post.content.join("\n\n"),
            category: post.category,
            date: post.date,
            readTime: post.readTime,
            image: post.image,
            author: post.author,
            featured: post.featured,
          });
        }
      });
    }
  }, [isEdit, params.id, reset]);

  const onSubmit = async (data: FormData) => {
    setSaving(true);
    try {
      const content = data.contentText
        .split(/\n\s*\n/)
        .map((s) => s.trim())
        .filter(Boolean);

      const body = {
        slug: data.slug,
        title: data.title,
        excerpt: data.excerpt,
        content,
        category: data.category,
        date: data.date,
        readTime: data.readTime,
        image: data.image,
        author: data.author,
        featured: data.featured,
      };

      if (isEdit) {
        await api.put(`/blog-posts/${params.id}`, body);
      } else {
        await api.post("/blog-posts", body);
      }
      navigate("/admin/blog-posts");
    } catch (err: any) {
      alert(err.response?.data?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Link href="/admin/blog-posts" className="text-white/40 hover:text-white transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <h1 className="text-2xl font-bold text-white font-[Rubik]">
          {isEdit ? "Edit Post" : "New Post"}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="max-w-3xl space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-white/60 mb-1.5">Title</label>
            <input
              {...register("title")}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#66CDAA]/50 outline-none"
              dir="rtl"
              required
            />
          </div>
          <div>
            <label className="block text-sm text-white/60 mb-1.5">Slug</label>
            <input
              {...register("slug")}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#66CDAA]/50 outline-none"
              dir="ltr"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm text-white/60 mb-1.5">Excerpt</label>
          <textarea
            {...register("excerpt")}
            rows={2}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#66CDAA]/50 outline-none resize-none"
            dir="rtl"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-white/60 mb-1.5">Category</label>
            <select
              {...register("category")}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#66CDAA]/50 outline-none"
            >
              <option value="seo">SEO</option>
              <option value="web">בניית אתרים</option>
              <option value="marketing">שיווק דיגיטלי</option>
              <option value="ai">AI</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-white/60 mb-1.5">Date</label>
            <input
              type="date"
              {...register("date")}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#66CDAA]/50 outline-none"
              dir="ltr"
              required
            />
          </div>
          <div>
            <label className="block text-sm text-white/60 mb-1.5">Read time (min)</label>
            <input
              type="number"
              {...register("readTime", { valueAsNumber: true })}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#66CDAA]/50 outline-none"
              min={1}
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-white/60 mb-1.5">Author</label>
            <input
              {...register("author")}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#66CDAA]/50 outline-none"
              dir="rtl"
              required
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-3 px-4 py-3 cursor-pointer">
              <input
                type="checkbox"
                {...register("featured")}
                className="w-4 h-4 rounded accent-[#66CDAA]"
              />
              <span className="text-sm text-white/60">Featured post</span>
            </label>
          </div>
        </div>

        <div>
          <label className="block text-sm text-white/60 mb-1.5">Image</label>
          <ImageUpload value={imageValue} onChange={(url) => setValue("image", url)} />
        </div>

        <div>
          <label className="block text-sm text-white/60 mb-1.5">
            Content (separate paragraphs with blank lines)
          </label>
          <textarea
            {...register("contentText")}
            rows={16}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#66CDAA]/50 outline-none resize-y font-mono text-sm leading-relaxed"
            dir="rtl"
            required
          />
        </div>

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={saving}
            className="bg-[#66CDAA] hover:bg-[#57b899] text-[#0F172A] font-bold px-6 py-3 rounded-xl transition-colors disabled:opacity-50"
          >
            {saving ? "Saving..." : isEdit ? "Update" : "Create"}
          </button>
          <Link
            href="/admin/blog-posts"
            className="px-6 py-3 rounded-xl border border-white/10 text-white/50 hover:text-white hover:border-white/20 transition-colors"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
